/**
 * Réduction conditionnelle
 *
 * Multiplicateur : chaque multiple de requiredQty débloque un quota de
 *                  discountedQty produits à prix réduit.
 *
 * Deux contextes distincts de calcul de la quantité requise :
 *
 *   1. Affichage hors bloc additionnel (cartes produit, prix fiche, boutons ATC,
 *      sticky bar, etc.) → quantité requise = panier uniquement.
 *
 *   2. Affichage à l'intérieur du bloc de produits additionnels
 *      ([data-bundle-price]) → quantité requise = panier
 *      + quantité sélectionnée via le sélecteur de quantité de la fiche produit
 *        (par défaut 1 si pas de sélecteur)
 *      + quantité des produits cochés dans le bloc additionnel.
 *
 * Plusieurs règles : toutes les entrées du metaobject sont chargées. Chaque
 *   entrée définit une règle indépendante (requiredProductIds, requiredQty,
 *   discountedProductIds, discountedQty, discountPct). Pour un produit ciblé
 *   par plusieurs règles, la meilleure réduction active gagne (hors bloc) ;
 *   dans le bloc additionnel, les règles sont appliquées dans l'ordre.
 *
 * Allocation cheapest-first (blocs additionnels) :
 *   Les produits cochés consomment les slots en priorité.
 *   Si le total dépasse le quota, les moins chers bénéficient de la réduction.
 *   L'allocation est globale pour une règle donnée.
 *
 * Contenus dynamiques : la réduction est réappliquée après chargement des blocs
 *   « récemment consultés », « produits complémentaires » et quick view.
 *
 * Config injectée par snippets/conditional-discount-config.liquid via window.__conditionalDiscount.
 */

(function () {
  "use strict";

  const config = window.__conditionalDiscount;
  if (!config) return;
  const rules = Array.isArray(config.rules) ? config.rules : [];
  if (!rules.length) return;

  // ─── Formatage monétaire ─────────────────────────────────────────────────────

  function formatMoney(cents) {
    const fmt = theme?.settings?.currencyCodeEnabled
      ? theme?.settings?.moneyWithCurrencyFormat
      : theme?.settings?.moneyFormat;
    if (window.theme?.Currency?.formatMoney && fmt) {
      return theme.Currency.formatMoney(cents, fmt);
    }
    return (cents / 100).toFixed(2);
  }

  // ─── HTML badge ─────────────────────────────────────────────────────────────

  function badgeHtml(price, comparePrice, extraClass) {
    if (!config.saveAmountEnabled || !comparePrice || comparePrice <= price)
      return "";
    const cls =
      "badge" +
      (extraClass || "") +
      " badge--onsale flex items-center gap-1d5 font-medium leading-none rounded-full";

    if (price === 0 && config.freeDisplayMode === "price_free") {
      return "";
    }
    if (price === 0 && config.freeDisplayMode === "badge_free") {
      return `<span class="${cls}">${config.i18n.free}</span>`;
    }
    if (config.saveType === "label") {
      return `<span class="${cls}">${config.i18n.sale}</span>`;
    }
    if (config.saveType === "dollar") {
      const saved = formatMoney(comparePrice - price);
      return `<span class="${cls}">${config.i18n.saveHtml.replace("__amount__", saved)}</span>`;
    }
    const pct = Math.round(((comparePrice - price) * 100) / comparePrice);
    return `<span class="${cls}">${config.i18n.saveHtml.replace("__amount__", pct + "%")}</span>`;
  }

  // ─── HTML prix ───────────────────────────────────────────────────────────────

  function regularPriceText(price) {
    if (price === 0 && config.freeDisplayMode === "price_free") {
      return config.i18n.free;
    }
    return formatMoney(price);
  }

  function regularPriceClass(price) {
    return price === 0 && config.freeDisplayMode === "price_free"
      ? "price__regular price__regular--free whitespace-nowrap"
      : "price__regular whitespace-nowrap";
  }

  // Prix remisé (avec prix barré). includeBadge = true pour les éléments bundle.
  function discountedMarkup(price, cap, includeBadge) {
    return (
      `<span class="sr-only">${config.i18n.salePriceLabel}</span>` +
      `<span class="${regularPriceClass(price)}">${regularPriceText(price)}</span>` +
      `<span class="sr-only">${config.i18n.regularPriceLabel}</span>` +
      `<span class="price__sale inline-flex items-center h-auto relative">${formatMoney(cap)}</span>` +
      (includeBadge ? badgeHtml(price, cap) : "")
    );
  }

  // Prix normal (restauration). includeBadge = true pour les blocs additionnels.
  function regularMarkup(price, compareAtPrice, includeBadge) {
    const priceFmt = regularPriceText(price);
    const priceCls = regularPriceClass(price);
    if (compareAtPrice > price) {
      return (
        `<span class="sr-only">${config.i18n.salePriceLabel}</span>` +
        `<span class="${priceCls}">${priceFmt}</span>` +
        `<span class="sr-only">${config.i18n.regularPriceLabel}</span>` +
        `<span class="price__sale inline-flex items-center h-auto relative">${formatMoney(compareAtPrice)}</span>` +
        (includeBadge ? badgeHtml(price, compareAtPrice) : "")
      );
    }
    return `<span class="${priceCls}">${priceFmt}</span>`;
  }

  // ─── Mise à jour éléments prix ───────────────────────────────────────────────

  // .price[data-base-price] — rendu par product-price.liquid
  function updateRegularPrice(el, active, discountPct) {
    const base = parseInt(el.dataset.basePrice, 10);
    const cap = parseInt(el.dataset.baseCompareAtPrice, 10) || 0;
    if (isNaN(base)) return;

    if (active) {
      const disc = Math.round((base * (100 - discountPct)) / 100);
      const ref = cap > base ? cap : base;
      el.classList.add("price--on-sale");
      el.innerHTML = discountedMarkup(disc, ref, false);
      el.setAttribute("data-cd-applied", "");
    } else {
      el.classList.toggle("price--on-sale", cap > base);
      el.innerHTML = regularMarkup(base, cap);
      el.removeAttribute("data-cd-applied");
    }
  }

  // Prix courant d'un bundle (variante sélectionnée) ou prix min Liquid
  function getBundleBasePrice(el) {
    const current = parseInt(el.dataset.currentPrice, 10);
    if (!isNaN(current) && current > 0) return current;
    return parseInt(el.dataset.priceMin, 10) || 0;
  }

  // [data-bundle-price] — blocs produits additionnels
  function updateBundlePrice(el, active, discountPct) {
    const base = getBundleBasePrice(el);
    const cap =
      parseInt(el.dataset.currentComparePrice, 10) ||
      parseInt(el.dataset.comparePrice, 10) ||
      0;
    if (isNaN(base) || base <= 0) return;

    const bundleItem = el.closest("[data-bundle-item]");
    const checkbox = bundleItem?.querySelector('input[name="bundles"]');

    // [CUSTOM] Placeholder « sélectionner une variante » (custom_preselect-variant) :
    // produit coché sans variante choisie + prix variables → « Dès €… » (sans remise,
    // comme le rendu décoché) ; la remise s'appliquera après sélection.
    const pendingSelect = bundleItem?.querySelector(
      "[data-bundle-variant-select]",
    );
    const pendingOpt = pendingSelect?.selectedOptions?.[0];
    if (
      checkbox?.checked === true &&
      pendingOpt?.hasAttribute?.("data-bundle-variant-placeholder")
    ) {
      const pendingMin = parseInt(el.dataset.priceMin, 10) || 0;
      const pendingMax = parseInt(el.dataset.priceMax, 10) || 0;
      const pendingFromText = el.dataset.priceFrom;
      if (pendingMin > 0 && pendingMax > pendingMin && pendingFromText) {
        // tiered-bundle actif : il fige et rend lui-même le « Dès … » (prix +
        // data-price) — ne pas écraser son rendu ni le prix figé.
        if (window.__tieredBundle) return;
        el.classList.remove("price--on-sale");
        el.dataset.currentComparePrice = "";
        el.dataset.currentPrice = "";
        const pendingUpTo = el.dataset.upToDiscount;
        el.innerHTML = pendingUpTo
          ? `<span class="price__regular price__regular--from whitespace-nowrap">${pendingFromText}</span><span class="price__discount-range whitespace-nowrap">${pendingUpTo}</span>`
          : `<span class="price__regular price__regular--from whitespace-nowrap">${pendingFromText}</span>`;
        checkbox.setAttribute("data-price", pendingMin);
        return;
      }
    }

    if (active) {
      const disc = Math.round((base * (100 - discountPct)) / 100);
      const ref = cap > base ? cap : base;
      el.classList.add("price--on-sale");
      el.innerHTML = discountedMarkup(disc, ref, true); // badge inclus dans le prix du bundle
      // Synchronise data-price sur la checkbox pour que bundle:change utilise le prix remisé
      if (checkbox) checkbox.setAttribute("data-price", disc);
    } else {
      el.classList.toggle("price--on-sale", cap > base);
      // Fallback : badge promo Shopify de base quand la réduction conditionnelle n'est plus active
      el.innerHTML = regularMarkup(base, cap, true);
      if (checkbox) checkbox.setAttribute("data-price", base);
    }
  }

  // .badges[data-product-id] — rendu par product-badges.liquid
  //
  // Lorsque la réduction conditionnelle est active, le badge natif (ex. -20 %) est masqué
  // et remplacé par un badge calculé du prix barré (compare_at_price) jusqu'au prix final
  // après réduction conditionnelle.
  // Ex. : prix barré 10 €, prix de base 8 €, réduction conditionnelle -50 %
  //   → prix conditionnel = 4 €
  //   → badge affiché = -60 % (de 10 € à 4 €), et non -50 % (uniquement la remise CD)
  function updateBadgesForProduct(productId, active, discountPct) {
    const priceEl = document.querySelector(
      `.price[data-product-id="${productId}"][data-base-price]:not([data-bundle-price]):not([data-bundle-pack-expand])`,
    );
    const base = parseInt(priceEl?.dataset.basePrice, 10);
    if (isNaN(base)) return;

    // Référence pour le calcul du badge : compare_at_price si disponible (prix barré Shopify),
    // sinon prix de base → permet d'afficher le taux total (ex. -60 % de 10 € à 4 €).
    const capFromAttr = parseInt(priceEl?.dataset.baseCompareAtPrice, 10) || 0;

    // Si le compare_at n'est pas sur l'élément sélectionné, chercher dans les variantes JSON
    // (cas produit multi-variantes où la variante sélectionnée n'a pas de prix barré propre
    // mais une autre variante au même prix en a un)
    let cap = capFromAttr;
    if (cap <= base) {
      // Chercher le compare_at_price maximal dans les variantes JSON de la page
      // (toutes les variantes de la page sont pour le même produit)
      const variantDataEl = document.querySelector(
        "script[data-product-variant-data]",
      );
      if (variantDataEl) {
        try {
          const variantData = JSON.parse(variantDataEl.textContent);
          const variants = variantData.productVariants || [];
          let maxCompareAt = 0;
          variants.forEach((v) => {
            if (
              v.compare_at_price > v.price &&
              v.compare_at_price > maxCompareAt
            ) {
              maxCompareAt = v.compare_at_price;
            }
          });
          if (maxCompareAt > base) cap = maxCompareAt;
        } catch (_) {}
      }
    }

    document
      .querySelectorAll(`.badges[data-product-id="${productId}"]`)
      .forEach((badgesEl) => {
        const existing = badgesEl.querySelector("[data-cd-badge]");
        if (existing) existing.remove();

        const nativeBadges = badgesEl.querySelectorAll(
          ".badge--onsale:not([data-cd-badge])",
        );

        if (!active || !config.saveAmountEnabled) {
          nativeBadges.forEach((badge) => {
            badge.style.display = "";
          });
          return;
        }

        nativeBadges.forEach((badge) => {
          badge.style.display = "none";
        });

        const disc = Math.round((base * (100 - discountPct)) / 100);
        // badgeCompare = prix barré si disponible, sinon prix de base
        const badgeCompare = cap > base ? cap : base;
        const isVert = !!badgesEl.querySelector(".badge--vertical");
        const html = badgeHtml(
          disc,
          badgeCompare,
          isVert ? " badge--vertical" : "",
        );
        if (!html) return;

        const tmp = document.createElement("div");
        tmp.innerHTML = html;
        const badge = tmp.firstElementChild;
        if (badge) {
          badge.dataset.cdBadge = "1";
          badgesEl.appendChild(badge);
        }
      });
  }

  // ─── État panier ─────────────────────────────────────────────────────────────

  // Initialisé depuis le Liquid pour éviter le délai du fetch asynchrone
  let cartData = config.cartData || null;

  function cartSumFor(ids) {
    if (!cartData) return 0;
    return cartData.items.reduce(
      (s, item) => (ids.includes(item.product_id) ? s + item.quantity : s),
      0,
    );
  }

  // Quantité sélectionnée sur la fiche produit (page where the bundle lives).
  // Par défaut 1 si pas de sélecteur. 0 hors d'une page produit.
  function pageProductQty() {
    const productInfo = document.querySelector("product-info[data-product-id]");
    if (!productInfo) return 0;
    const pageId = parseInt(productInfo.dataset.productId, 10);
    const qtyInput = document.querySelector(
      `quantity-input[data-product-id="${pageId}"] input[type="number"]`,
    );
    if (!qtyInput) return 1;
    const value = parseInt(qtyInput.value, 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  // Nombre de produits cochés dans les blocs additionnels dont le produit
  // appartient aux requiredProductIds d'une règle donnée (chaque case = 1).
  // Seuls les cochés « requis » contribuent au seuil de la règle : un coché
  // simplement éligible ne déclenche pas la réduction de lui-même.
  function checkedRequiredCountForRule(rule) {
    let count = 0;
    document
      .querySelectorAll('[data-bundle-item] input[name="bundles"]:checked')
      .forEach((cb) => {
        const bundleItem = cb.closest("[data-bundle-item]");
        const productId = parseInt(bundleItem?.dataset.productId, 10);
        if (
          Number.isFinite(productId) &&
          rule.requiredProductIds.includes(productId)
        ) {
          count++;
        }
      });
    return count;
  }

  // [CUSTOM] Contribution d'un pack sélectionné (bundle-picker.js) au calcul CD
  // DU BLOC ADDITIONNEL uniquement, via window.__bundlePackCD :
  //  • packs (suppressPage:true) : le pack est indépendant → on ne compte pas le
  //    produit de la fiche (ni de supplément), seulement le panier ;
  //  • composé (addItems) : on ajoute les produits supplémentaires du pack aux
  //    quantités requises / remisées (le produit de la fiche reste compté via
  //    pageProductQty). Renvoie un effet neutre si aucun pack sélectionné.
  function packCDForRule(rule) {
    const p = window.__bundlePackCD;
    const out = { suppressPage: false, extraReq: 0, extraDisc: 0 };
    if (!p) return out;
    out.suppressPage = !!p.suppressPage;
    (p.addItems || []).forEach((it) => {
      const q = it.quantity || 1;
      if (rule.requiredProductIds.includes(it.productId)) out.extraReq += q;
      if (rule.discountedProductIds.includes(it.productId)) out.extraDisc += q;
    });
    return out;
  }

  // ─── Boutons ATC / sticky (product-buy-price) ────────────────────────────────

  function getProductIdForBuyPrice(el) {
    const nested = el.querySelector(".price[data-product-id][data-base-price]");
    if (nested) return parseInt(nested.dataset.productId, 10);

    const formId = el.getAttribute("form");
    if (!formId) return null;
    const form = document.getElementById(formId);
    const scope = form?.closest(".shopify-section, .quick-view, .x-modal");
    const productInfo = scope?.querySelector("product-info[data-product-id]");
    return productInfo ? parseInt(productInfo.dataset.productId, 10) : null;
  }

  function syncBuyPriceBase(el) {
    const nested = el.querySelector(".price[data-base-price]");
    if (nested) {
      el.dataset.basePrice = nested.dataset.basePrice;
      el.dataset.baseCompareAtPrice = nested.dataset.baseCompareAtPrice || "0";
    } else if (!el.dataset.basePrice) {
      el.dataset.basePrice = el.getAttribute("data-price");
      el.dataset.baseCompareAtPrice = "0";
    }
  }

  function updateProductBuyPriceEl(el, active, discountPct) {
    syncBuyPriceBase(el);
    const base = parseInt(el.dataset.basePrice, 10);
    const cap = parseInt(el.dataset.baseCompareAtPrice, 10) || 0;
    if (isNaN(base)) return;

    const price = active
      ? Math.round((base * (100 - discountPct)) / 100)
      : base;
    el.setAttribute("data-price", price);

    const nested = el.querySelector(".price[data-base-price]");
    if (nested) {
      updateRegularPrice(nested, active, discountPct);
    } else if (active) {
      const ref = cap > base ? cap : base;
      el.innerHTML =
        cap > base
          ? discountedMarkup(price, ref, false)
          : `<span class="price__regular whitespace-nowrap">${formatMoney(price)}</span>`;
    } else {
      el.innerHTML =
        cap > base
          ? regularMarkup(base, cap)
          : `<span class="price__regular whitespace-nowrap">${formatMoney(base)}</span>`;
    }

    // ProductBuyPrice restaure _originalHTML quand aucun bundle n'est coché
    if ("_originalHTML" in el) {
      el._originalHTML = el.innerHTML;
    }
  }

  // ─── Application des prix ─────────────────────────────────────────────────────

  // Met à jour les cartes / prix fiche pour un produit donné selon l'état calculé.
  function applyRegularPriceForProduct(productId, active, discountPct) {
    document
      .querySelectorAll(
        `.price[data-product-id="${productId}"][data-base-price]`,
      )
      .forEach((el) => {
        if (el.hasAttribute("data-bundle-price")) return;
        if (el.hasAttribute("data-bundle-pack-expand")) return;
        updateRegularPrice(el, active, discountPct);
      });

    document
      .querySelectorAll(
        `[data-product-id="${productId}"] .price[data-base-price]:not([data-product-id])`,
      )
      .forEach((el) => {
        if (
          el.hasAttribute("data-bundle-price") ||
          el.closest("[data-bundle-price]") ||
          el.hasAttribute("data-bundle-pack-expand")
        )
          return;
        updateRegularPrice(el, active, discountPct);
      });

    updateBadgesForProduct(productId, active, discountPct);
  }

  function applyProductBuyPriceForProduct(productId, active, discountPct) {
    // [CUSTOM] Pack actif : le prix ATC est géré par bundle-picker.js.
    if (window.__bundlePackCD != null) return;
    document.querySelectorAll("product-buy-price[data-price]").forEach((el) => {
      if (
        el.closest("product-sticky-form") ||
        el.hasAttribute("data-sticky-atc-price")
      )
        return;
      const elProductId = getProductIdForBuyPrice(el);
      if (elProductId !== productId) return;
      // Pack expand : prix ATC géré par bundle-pack-pricing.js
      const formId = el.getAttribute("form");
      const form = formId ? document.getElementById(formId) : null;
      if (form?.getAttribute("data-bundle-expand") === "true") return;
      updateProductBuyPriceEl(el, active, discountPct);
    });
  }

  /**
   * Détermine si un item bundle bénéficie de la réduction conditionnelle pour
   * une règle donnée (même logique que applyBundlePricesForRule).
   */
  function resolveBundleItemCdDiscount(
    item,
    rule,
    baseBundleReq,
    cartDisc,
    checked,
    unchecked,
  ) {
    const mult = Math.floor(baseBundleReq / rule.requiredQty);
    const totalSlots = mult * rule.discountedQty;
    const slotsAfterCart = Math.max(0, totalSlots - cartDisc);

    const hasPriorityLookahead = (it) => {
      if (!it.isRequired) return false;
      const hypoBundleReq = baseBundleReq + 1;
      const hypoMult = Math.floor(hypoBundleReq / rule.requiredQty);
      if (hypoMult <= mult) return false;
      const hypoSlots = hypoMult * rule.discountedQty;
      const hypoSlotsAfterCart = Math.max(0, hypoSlots - cartDisc);
      let rank = 0;
      for (const c of checked) {
        if (c.price <= it.price) rank++;
      }
      return rank < hypoSlotsAfterCart;
    };

    if (slotsAfterCart === 0) {
      return !item.isChecked && hasPriorityLookahead(item);
    }

    if (checked.length < slotsAfterCart) {
      return true;
    }

    if (item.isChecked) {
      const idx = checked.findIndex((c) => c.el === item.el);
      return idx >= 0 && idx < slotsAfterCart;
    }

    return hasPriorityLookahead(item);
  }

  function collectBundleItemsForRule(rule) {
    const allItems = [];
    for (const productId of rule.discountedProductIds) {
      const isRequired = rule.requiredProductIds.includes(productId);
      document
        .querySelectorAll(`[data-bundle-price][data-product-id="${productId}"]`)
        .forEach((el) => {
          const bundleItemEl = el.closest("[data-bundle-item]");
          const checkbox = bundleItemEl?.querySelector('input[name="bundles"]');
          allItems.push({
            el,
            isRequired,
            price: getBundleBasePrice(el),
            isChecked: checkbox?.checked === true,
          });
        });
    }
    return allItems;
  }

  /** État CD pour un [data-bundle-price] (utilisé par tiered-bundle.js). */
  function computeBundleItemCdState(el) {
    const productId = parseInt(el.dataset.productId, 10);
    if (!Number.isFinite(productId)) return { active: false, discountPct: 0 };

    let active = false;
    let discountPct = 0;

    for (const rule of rules) {
      if (!rule.discountedProductIds.includes(productId)) continue;

      const cartReq = cartSumFor(rule.requiredProductIds);
      const cartDisc = cartSumFor(rule.discountedProductIds);
      const checkedReq = checkedRequiredCountForRule(rule);
      const pk = packCDForRule(rule);
      const baseBundleReq =
        cartReq + (pk.suppressPage ? 0 : pageProductQty()) + checkedReq + pk.extraReq;
      const effCartDisc = cartDisc + pk.extraDisc;
      const allItems = collectBundleItemsForRule(rule);
      if (!allItems.length) continue;

      const item = allItems.find((i) => i.el === el);
      if (!item) continue;

      const checked = allItems
        .filter((i) => i.isChecked)
        .sort((a, b) => a.price - b.price);
      const unchecked = allItems.filter((i) => !i.isChecked);

      if (
        resolveBundleItemCdDiscount(
          item,
          rule,
          baseBundleReq,
          effCartDisc,
          checked,
          unchecked,
        )
      ) {
        if (rule.discountPct > discountPct) {
          discountPct = rule.discountPct;
          active = true;
        }
      }
    }

    return { active, discountPct };
  }

  function evaluateBundlePricesOnly() {
    const pageSelectorQty = pageProductQty();
    for (const rule of rules) {
      const cartReq = cartSumFor(rule.requiredProductIds);
      const cartDisc = cartSumFor(rule.discountedProductIds);
      const checkedReq = checkedRequiredCountForRule(rule);
      const pk = packCDForRule(rule);
      const baseBundleReq =
        cartReq + (pk.suppressPage ? 0 : pageSelectorQty) + checkedReq + pk.extraReq;
      applyBundlePricesForRule(rule, baseBundleReq, cartDisc + pk.extraDisc);
    }
    refireBundleChange();
  }

  window.__cdBundlePricing = {
    computeItemState: computeBundleItemCdState,
    applyAll: evaluateBundlePricesOnly,
  };

  /**
   * Allocation des réductions dans le bloc de produits additionnels (par règle).
   *
   * Calcul des slots :
   *   slots          = floor(baseBundleReq / requiredQty) × discountedQty
   *   slotsAfterCart = max(0, slots − cartDisc)
   *
   * Régimes :
   *
   *  • « Limite non atteinte » : checked.length < slotsAfterCart
   *    Il reste de la place ; tous les produits éligibles (cochés ET non
   *    cochés) sont mis à prix réduit.
   *
   *  • « Limite atteinte »   : checked.length ≥ slotsAfterCart
   *    On retire la réduction sur les non cochés ; parmi les cochés on garde
   *    la réduction en cheapest-first jusqu'à épuisement des slots.
   *
   *  • slotsAfterCart = 0 : aucun slot pour le moment.
   *
   * Priorité « requis + éligible non coché » :
   *   Si un produit non coché P est À LA FOIS requis ET éligible, et que le
   *   fait de le cocher débloquerait un nouveau multiple (hypoMult > mult),
   *   alors on l'affiche réduit même si la limite actuelle est saturée — à
   *   condition qu'il rentre dans les slots hypothétiques en cheapest-first
   *   par rapport aux cochés existants. Cocher P créera de nouveaux slots
   *   dont P pourra lui-même bénéficier.
   *
   * @param {object} rule           - règle (discountedProductIds, requiredQty…)
   * @param {number} baseBundleReq  - cartReq + pageQty + checkedRequiredCount actuel
   * @param {number} cartDisc       - articles déjà dans le panier (slots consommés)
   */
  function applyBundlePricesForRule(rule, baseBundleReq, cartDisc) {
    const allItems = collectBundleItemsForRule(rule);
    if (!allItems.length) return;

    const checked = allItems
      .filter((i) => i.isChecked)
      .sort((a, b) => a.price - b.price);
    const unchecked = allItems.filter((i) => !i.isChecked);

    for (const item of allItems) {
      const active = resolveBundleItemCdDiscount(
        item,
        rule,
        baseBundleReq,
        cartDisc,
        checked,
        unchecked,
      );
      updateBundlePrice(item.el, active, rule.discountPct);
    }
  }

  // Recalcule le total affiché sur les boutons ATC / sticky (product-buy-price)
  function refireBundleChange() {
    document
      .querySelectorAll('[data-type="add-to-cart-form"]')
      .forEach((form) => {
        const scope = form.closest(".shopify-section, .quick-view, .x-modal");
        const checkedBundles = scope
          ? Array.from(scope.querySelectorAll('input[name="bundles"]:checked'))
          : [];

        form.dispatchEvent(
          new CustomEvent("bundle:change", {
            detail: {
              items: checkedBundles.map((input) => ({
                id: input.value,
                quantity: 1,
                price: input.getAttribute("data-price"),
              })),
            },
          }),
        );
      });
    if (typeof window.__syncStickyBuyPricesFromAtc === "function") {
      window.__syncStickyBuyPricesFromAtc();
    }
  }

  // Identifie le produit affiché par la fiche produit courante (le cas échéant),
  // pour calibrer la quantité « look-ahead » par produit.
  function getPageProductId() {
    const productInfo = document.querySelector("product-info[data-product-id]");
    if (!productInfo) return null;
    const id = parseInt(productInfo.dataset.productId, 10);
    return Number.isFinite(id) ? id : null;
  }

  function evaluateAndApply() {
    // ── Contexte « hors bloc additionnel » ───────────────────────────────────
    // Pré-calcul de l'état par produit avec look-ahead :
    // pour chaque produit éligible P, on évalue si l'ajout de P (avec la
    // quantité sélectionnée sur la fiche si P est le produit affiché, sinon 1)
    // permettrait de franchir le seuil et de débloquer un slot pour P.
    //
    // Cas couvert : produit à la fois requis ET éligible. Son propre ajout peut
    // suffire à déclencher la réduction conditionnelle ; on l'affiche alors
    // directement à prix réduit partout où il apparaît.
    const outsideState = new Map(); // productId -> { active, discountPct }
    const pageProductId = getPageProductId();
    const pageSelectorQty = pageProductQty();

    for (const rule of rules) {
      const cartReq = cartSumFor(rule.requiredProductIds);
      const cartDisc = cartSumFor(rule.discountedProductIds);

      for (const pid of rule.discountedProductIds) {
        const isRequired = rule.requiredProductIds.includes(pid);
        // Quantité hypothétiquement ajoutée : sélecteur de la fiche si P est
        // affiché en fiche, sinon 1 (ajout simple depuis carte, reco, etc.).
        const lookAheadQty =
          pid === pageProductId && pageSelectorQty > 0 ? pageSelectorQty : 1;
        const effReq = cartReq + (isRequired ? lookAheadQty : 0);
        const effDisc = cartDisc + lookAheadQty;
        const mult = Math.floor(effReq / rule.requiredQty);
        const slots = mult * rule.discountedQty;
        // Au moins un slot disponible pour P après prise en compte du panier
        const active = mult > 0 && slots - cartDisc > 0;

        const cur = outsideState.get(pid);
        if (active) {
          if (!cur?.active || rule.discountPct > cur.discountPct) {
            outsideState.set(pid, {
              active: true,
              discountPct: rule.discountPct,
            });
          }
        } else if (!cur) {
          // marque le produit pour réinitialisation si aucune autre règle ne l'active
          outsideState.set(pid, {
            active: false,
            discountPct: rule.discountPct,
          });
        }
      }
    }

    for (const [productId, state] of outsideState) {
      applyRegularPriceForProduct(productId, state.active, state.discountPct);
      applyProductBuyPriceForProduct(
        productId,
        state.active,
        state.discountPct,
      );
    }

    // Expose l'état outside-ctx pour les modules en aval (ex. tiered-bundle.js)
    // qui doivent connaître la remise CD appliquée par produit afin de la
    // combiner avec la leur (la plus forte gagne côté backend).
    window.__cdAppliedState = outsideState;

    // ── Contexte « bloc additionnel » ────────────────────────────────────────
    // Chaque règle alloue ses propres slots aux éléments [data-bundle-price]
    // qu'elle cible. La quantité requise comprend uniquement les cochés
    // « requis » : un produit simplement éligible coché ne contribue PAS au
    // déclenchement, il consomme seulement un slot s'il y en a un disponible.
    // En cas de chevauchement entre règles, la dernière appliquée l'emporte.
    for (const rule of rules) {
      const cartReq = cartSumFor(rule.requiredProductIds);
      const cartDisc = cartSumFor(rule.discountedProductIds);
      const checkedReq = checkedRequiredCountForRule(rule);
      // [CUSTOM] Contribution du pack bundle sélectionné (même logique que
      // evaluateBundlePricesOnly) : pack natif → ne pas compter la fiche ;
      // composé → ajouter les suppléments. Nécessaire ici pour rester correct
      // après un cart:updated (ce evaluate complet tourne aussi hors sélection).
      const pk = packCDForRule(rule);
      const baseBundleReq =
        cartReq + (pk.suppressPage ? 0 : pageSelectorQty) + checkedReq + pk.extraReq;

      applyBundlePricesForRule(rule, baseBundleReq, cartDisc + pk.extraDisc);
    }

    // Répercute les data-price mis à jour dans le total du bouton ATC et du sticky
    refireBundleChange();
  }

  // ─── Init ─────────────────────────────────────────────────────────────────────

  async function fetchCart() {
    try {
      const res = await fetch("/cart.js");
      cartData = await res.json();
    } catch (_) {}
  }

  function syncCartFromEvent(cart) {
    if (cart && Array.isArray(cart.items)) {
      cartData = cart;
    }
  }

  function applyCartUpdateFromEvent(cart) {
    syncCartFromEvent(cart);
    // ProductBundleDetails décoche les bundles sur cartUpdate (source product-form)
    // avant le recalcul — attendre la fin de ce cycle DOM.
    Promise.resolve().then(() => {
      evaluateAndApply();
    });
  }

  function bindEvents() {
    document.addEventListener("cart:updated", (e) => {
      applyCartUpdateFromEvent(e.detail?.cart);
    });

    if (window.theme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
      theme.pubsub.subscribe(
        theme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        (payload) => {
          applyCartUpdateFromEvent(payload?.cart);
        },
      );
    }

    // Changement de variante (pubsub thème)
    if (window.theme?.pubsub?.PUB_SUB_EVENTS?.variantChange) {
      theme.pubsub.subscribe(
        theme.pubsub.PUB_SUB_EVENTS.variantChange,
        evaluateAndApply,
      );
    }

    // Quantité sélectionnée sur la fiche (bloc additionnels)
    document.addEventListener("input", (e) => {
      if (
        e.target.matches('input[type="number"]') &&
        e.target.closest("quantity-input")
      ) {
        evaluateAndApply();
      }
    });

    // Checkbox, variante ou quantité du bloc additionnels.
    // Microtask pour s'exécuter APRÈS custom_product-card.js (updatePriceDisplays).
    document.addEventListener("change", (e) => {
      if (
        e.target.matches('input[name="bundles"]') ||
        e.target.matches("[data-bundle-variant-select]") ||
        (e.target.closest("[data-bundle-item]") &&
          !e.target.matches('input[name="bundles"]'))
      ) {
        Promise.resolve().then(evaluateAndApply);
      }
    });

    // Grille collection rechargée (filtres AJAX)
    document.addEventListener("collection:reloaded", () => {
      Promise.resolve().then(evaluateAndApply);
    });

    // Contenus chargés dynamiquement (capture pour attraper les événements non-bubbling)
    document.addEventListener("recentlyViewed:loaded", evaluateAndApply, true);
    document.addEventListener("recommendations:loaded", evaluateAndApply, true);

    // Modal : premier passage dès le HTML injecté, second après init des bundles
    document.addEventListener("quickview:loaded", evaluateAndApply);
    document.addEventListener("bundles:initialized", evaluateAndApply);
  }

  function init() {
    // cartData est déjà renseigné depuis Liquid (config.cartData) — pas de fetch bloquant
    evaluateAndApply();
    bindEvents();
    // Rafraîchissement en arrière-plan pour rester synchronisé
    fetchCart().then(() => evaluateAndApply());
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
